import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Check, Copy, Loader2 } from 'lucide-react';

import { toaster } from '@/shared/libs';
import { Button, Dialog } from '@/shared/ui';
import { useCreateRegistrationInvite } from '@entities/User';

export interface InviteUserModalProps {
  onClose: () => void;
}

export const InviteUserModal: FC<InviteUserModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const createInvite = useCreateRegistrationInvite();
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCreate = async (): Promise<void> => {
    try {
      const invite = await createInvite.mutateAsync();
      const url = invite.invite_url ?? '';

      setInviteUrl(url);
      toaster.success({ title: t('toast.inviteCreated') });
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.users.inviteFailed'))
          : t('errors.unknown'),
      });
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!inviteUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toaster.error({ title: t('errors.unknown') });
    }
  };

  return (
    <Dialog
      className="overflow-hidden"
      title={t('pages.admin.users.invite.title')}
      onClose={onClose}>
      <p className="mb-3 min-w-0 text-sm text-muted-foreground">
        {t('pages.admin.users.invite.description')}
      </p>

      {inviteUrl ? (
        <div className="min-w-0 max-w-full space-y-2 overflow-hidden">
          <div className="flex min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-xl border border-border bg-background/60 py-1.5 pl-3 pr-1.5">
            <span
              className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-foreground"
              title={inviteUrl}>
              {inviteUrl}
            </span>
            <Button
              aria-label={
                copied
                  ? t('pages.admin.users.invite.copied')
                  : t('pages.admin.users.invite.copy')
              }
              className="shrink-0"
              size="icon-sm"
              type="button"
              variant="brand"
              onClick={() => {
                void handleCopy();
              }}>
              {copied ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          {copied && (
            <p className="text-xs text-emerald-500">
              {t('pages.admin.users.invite.copied')}
            </p>
          )}
        </div>
      ) : (
        <Button
          className="w-full"
          disabled={createInvite.isPending}
          type="button"
          variant="brand"
          onClick={() => {
            void handleCreate();
          }}>
          {createInvite.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('buttons.invite')
          )}
        </Button>
      )}
    </Dialog>
  );
};

InviteUserModal.displayName = 'InviteUserModal';
